import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { 
  ExclamationCircleIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  InformationCircleIcon,
  UserPlusIcon,
  EnvelopeIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import BillingAuthModal from './BillingAuthModal';

const MembersSection = ({ user, organization, members, setMembers, userRole, toastStyle, darkMode }) => {
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [emailError, setEmailError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [resendingInvite, setResendingInvite] = useState(false);
  const [memberIdResending, setMemberIdResending] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [memberIdChangingRole, setMemberIdChangingRole] = useState(null);
  const [pendingRoleChange, setPendingRoleChange] = useState(null); // { member, newRole }
  const [removingMember, setRemovingMember] = useState(false);
  
  // CSV bulk invite states
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [showCsvSuccess, setShowCsvSuccess] = useState(false);
  
  // Billing authorization states
  const [showBillingAuth, setShowBillingAuth] = useState(false);
  const [billingDetails, setBillingDetails] = useState(null);
  const [checkingBilling, setCheckingBilling] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState(null);
  
  // Refs
  const deleteConfirmRef = useRef(null);

  // Email validation
  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) return 'Email address is required';
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  // First name validation
  const validateFirstName = (value) => {
    if (!value || !value.trim()) return 'First name is required';
    return '';
  };

  // Last name validation
  const validateLastName = (value) => {
    if (!value || !value.trim()) return 'Last name is required';
    return '';
  };

  // Calculate days until invitation expires (invitations expire after 7 days)
  const calculateExpirationDays = (invitedAt) => {
    if (!invitedAt) return null;
    
    const inviteDate = new Date(invitedAt);
    const expirationDate = new Date(inviteDate);
    expirationDate.setDate(expirationDate.getDate() + 7); // Invitations expire after 7 days
    
    const now = new Date();
    const timeDiff = expirationDate - now;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    
    return daysDiff;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setInviteEmail(value);
    // Clear error when user types (don't validate on every keystroke)
    if (emailError) setEmailError('');
  };

  const handleEmailBlur = () => {
    // Only validate when user leaves the field
    if (inviteEmail) {
      setEmailError(validateEmail(inviteEmail));
    }
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    setInviteFirstName(value);
    // Clear error when user types
    if (firstNameError) setFirstNameError('');
  };

  const handleFirstNameBlur = () => {
    // Only validate when user leaves the field
    if (inviteFirstName) {
      setFirstNameError(validateFirstName(inviteFirstName));
    }
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    setInviteLastName(value);
    // Clear error when user types
    if (lastNameError) setLastNameError('');
  };

  const handleLastNameBlur = () => {
    // Only validate when user leaves the field
    if (inviteLastName) {
      setLastNameError(validateLastName(inviteLastName));
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail.trim()) {
      setEmailError('Email is required');
      return;
    }
    
    if (!inviteFirstName.trim()) {
      setFirstNameError('First name is required');
      return;
    }
    
    if (!inviteLastName.trim()) {
      setLastNameError('Last name is required');
      return;
    }
    
    if (emailError || firstNameError || lastNameError) {
      return;
    }
    
    const inviteData = {
      email: inviteEmail,
      firstName: inviteFirstName.trim(),
      lastName: inviteLastName.trim(),
      role: inviteRole
    };
    
    try {
      setCheckingBilling(true);
      
      // First, check if billing authorization is required
      const billingCheckResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/check-team-member-billing`,
        inviteData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!billingCheckResponse.data.requiresBilling) {
        // No billing required - proceed with standard invitation
        setCheckingBilling(false);
        await sendDirectInvitation(inviteData);
      } else {
        // Billing required - show authorization modal
        setBillingDetails(billingCheckResponse.data.billingDetails);
        setPendingInviteData(inviteData);
        setShowBillingAuth(true);
        setCheckingBilling(false);
      }
      
    } catch (error) {
      console.error('Error checking billing requirements:', error);
      setCheckingBilling(false);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.message, toastStyle.error);
      } else {
        toast.error('Failed to check billing requirements. Please try again.', toastStyle.error);
      }
      // DO NOT send invitation if billing check fails
      return;
    }
  };

  const sendDirectInvitation = async (inviteData) => {
    try {
      setInviting(true);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/organization/members/invite`,
        inviteData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Axios automatically handles success/error - no need to check response.ok
      if (response.data) {
        setMembers([...members, {
          id: response.data.invitation.id,
          email: response.data.invitation.email,
          role: response.data.invitation.role,
          status: 'invited',
          invitedAt: response.data.invitation.invitedAt,
          firstName: response.data.invitation.firstName,
          lastName: response.data.invitation.lastName,
          name: `${response.data.invitation.firstName || ''} ${response.data.invitation.lastName || ''}`.trim() || null
        }]);
      }
      
      resetInviteForm();
      toast.success('Invitation sent successfully! The invitation will expire in 7 days.', toastStyle.success);
      
    } catch (error) {
      console.error('Error inviting member:', error);
      
      if (error.response?.status === 409) {
        setEmailError(error.response.data.message || 'This email is already invited');
      } else {
        toast.error('Failed to send invitation', toastStyle.error);
      }
    } finally {
      setInviting(false);
    }
  };

  const resetInviteForm = () => {
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInviteRole('member');
    setEmailError('');
    setFirstNameError('');
    setLastNameError('');
    setShowInviteForm(false);
  };

  const handleCloseBillingAuth = () => {
    setShowBillingAuth(false);
    setBillingDetails(null);
    setPendingInviteData(null);
  };

  const handleResendInvitation = async (memberId) => {
    try {
      setResendingInvite(true);
      setMemberIdResending(memberId);
      
      // Find the member to get their details
      const member = members.find(m => m.id === memberId);
      if (!member) {
        toast.error('Member not found', toastStyle.error);
        return;
      }
      
      // For expired invitations, we need to check billing authorization again
      const daysLeft = calculateExpirationDays(member.invitedAt);
      const isExpired = daysLeft !== null && daysLeft <= 0;
      
      if (isExpired) {
        // For expired invitations, check billing requirements (capture the hole again)
        try {
          setCheckingBilling(true);
          
          const billingCheckResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/team-billing/check-team-member-billing`,
            {
              email: member.email,
              firstName: member.firstName,
              lastName: member.lastName,
              role: member.role
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          setCheckingBilling(false);
          
          if (billingCheckResponse.data.requiresBilling) {
            // Show billing authorization modal for expired invitation
            setBillingDetails(billingCheckResponse.data.billingDetails);
            setPendingInviteData({
              email: member.email,
              firstName: member.firstName,
              lastName: member.lastName,
              role: member.role,
              isResend: true,
              memberId: memberId
            });
            setShowBillingAuth(true);
            return; // Don't continue with regular resend
          }
        } catch (billingError) {
          console.error('Error checking billing for expired invitation:', billingError);
          setCheckingBilling(false);
          // DO NOT continue with regular resend if billing is required but fails
          toast.error('Failed to check billing requirements for expired invitation. Please try again.', toastStyle.error);
          return;
        }
      }
      
      // Regular resend logic (for non-expired or non-billing invitations)
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/organization/members/${memberId}/resend-invitation`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Invitation resent successfully! The invitation will expire in 7 days.', toastStyle.success);
      
      // Update the invitation date in the members list
      const updatedMembers = members.map(member => {
        if (member.id === memberId) {
          return { 
            ...member, 
            invitedAt: new Date(),
            firstName: response.data.invitation.firstName,
            lastName: response.data.invitation.lastName,
            name: `${response.data.invitation.firstName || ''} ${response.data.invitation.lastName || ''}`.trim() || member.name
          };
        }
        return member;
      });
      
      setMembers(updatedMembers);
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation', toastStyle.error);
    } finally {
      setResendingInvite(false);
      setMemberIdResending(null);
    }
  };

  const handleChangeRole = async (member, role) => {
    if (!member || !role || role === member.role) return;
    
    try {
      setMemberIdChangingRole(member.id);
      
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/organization/members/${member.id}/role`,
        { role },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, role } : m));
      toast.success('Role updated', toastStyle.success);
      return true;
    } catch (error) {
      console.error('Error changing role:', error);
      if (error.response?.status === 403) {
        toast.error('Cannot change the role of the organization owner', toastStyle.error);
      } else {
        toast.error('Failed to update role', toastStyle.error);
      }
      return false;
    } finally {
      setMemberIdChangingRole(null);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/organization/members/${memberToRemove.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Remove member from list
      const updatedMembers = members.filter(member => member.id !== memberToRemove.id);
      setMembers(updatedMembers);
      
      // Reset states
      setShowDeleteConfirm(false);
      setMemberToRemove(null);
      
      toast.success('Member removed successfully', toastStyle.success);
    } catch (error) {
      console.error('Error removing member:', error);
      
      if (error.response?.status === 403) {
        toast.error(error.response.data.message || 'Cannot remove this member', toastStyle.error);
      } else {
        toast.error('Failed to remove member', toastStyle.error);
      }
    }
  };

  const handleCancelInvite = () => {
    setShowInviteForm(false);
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInviteRole('member');
    setEmailError('');
    setFirstNameError('');
    setLastNameError('');
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setMemberToRemove(null);
  };


  // CSV file handling functions
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please select a valid CSV file', toastStyle.error);
      return;
    }
    
    setCsvFile(file);
    parseCsvFile(file);
  };

  const parseCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        toast.error('CSV file is empty', toastStyle.error);
        return;
      }
      
      // Parse CSV data
      const data = [];
      const errors = [];
      
      // Skip header row if it exists
      const startIndex = lines[0].toLowerCase().includes('email') ? 1 : 0;
      
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const columns = line.split(',').map(col => col.trim().replace(/"/g, ''));
        
        if (columns.length < 3) {
          errors.push(`Row ${i + 1}: Missing required columns (email, firstName, lastName)`);
          continue;
        }
        
        const [email, firstName, lastName, role = 'member'] = columns;
        
        // Validate email
        const emailError = validateEmail(email);
        const firstNameError = validateFirstName(firstName);
        const lastNameError = validateLastName(lastName);
        
        if (emailError || firstNameError || lastNameError) {
          errors.push(`Row ${i + 1}: ${emailError || firstNameError || lastNameError}`);
          continue;
        }
        
        // Check for valid role
        const validRoles = ['member', 'admin'];
        const cleanRole = role.toLowerCase();
        if (!validRoles.includes(cleanRole)) {
          errors.push(`Row ${i + 1}: Invalid role "${role}". Must be "member" or "admin"`);
          continue;
        }
        
        data.push({
          email: email.toLowerCase(),
          firstName,
          lastName,
          role: cleanRole,
          rowNumber: i + 1
        });
      }
      
      setCsvData(data);
      setCsvErrors(errors);
      setShowCsvPreview(true);
    };
    
    reader.readAsText(file);
  };

  const handleBulkInvite = async () => {
    if (csvData.length === 0) {
      toast.error('No valid data to process', toastStyle.error);
      return;
    }
    
    try {
      setCsvProcessing(true);
      
      // First, check if billing authorization is required for bulk invites
      const billingCheckResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/check-bulk-member-billing`,
        { invitations: csvData },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      if (!billingCheckResponse.data.requiresBilling) {
        // No billing required - proceed with standard bulk invitation
        await processBulkInviteWithoutBilling(billingCheckResponse.data.validInvitations);
      } else {
        // Billing required - show authorization modal
        setBillingDetails({
          ...billingCheckResponse.data.billingDetails,
          isBulkInvite: true,
          memberInfo: billingCheckResponse.data.memberInfo
        });
        setPendingInviteData(billingCheckResponse.data.validInvitations);
        setShowBillingAuth(true);
        setCsvProcessing(false);
        
        // Keep the CSV modal open in background
        return;
      }
      
    } catch (error) {
      console.error('Error checking bulk billing requirements:', error);
      setCsvProcessing(false);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.message, toastStyle.error);
      } else {
        toast.error('Failed to check billing requirements. Please try again.', toastStyle.error);
      }
      return;
    }
  };

  // Process bulk invitations without billing (within plan limits)
  const processBulkInviteWithoutBilling = async (validInvitations) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/organization/members/bulk-invite`,
        { invitations: validInvitations },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const { successful, failed, invitations } = response.data;
      
      // Add successful invitations to members list
      if (invitations && invitations.length > 0) {
        const newMembers = invitations.map(inv => ({
          id: inv.id,
          email: inv.email,
          role: inv.role,
          status: 'invited',
          invitedAt: inv.invitedAt,
          firstName: inv.firstName,
          lastName: inv.lastName,
          name: `${inv.firstName || ''} ${inv.lastName || ''}`.trim() || null
        }));
        
        setMembers([...members, ...newMembers]);
      }
      
      setCsvResults({ successful, failed });
      
      // Show success state and auto-close modal
      if (successful > 0) {
        setShowCsvSuccess(true);
        toast.success(`Successfully sent ${successful} invitation${successful !== 1 ? 's' : ''}!`, toastStyle.success);
        
        // Auto-close modal with fade-out after showing success
        setTimeout(() => {
          resetCsvState();
        }, 2000);
      }
      
      if (failed > 0) {
        toast.error(`Failed to send ${failed} invitation${failed !== 1 ? 's' : ''}`, toastStyle.error);
      }
      
    } catch (error) {
      console.error('Error with bulk invite:', error);
      toast.error('Failed to process bulk invitations', toastStyle.error);
    } finally {
      setCsvProcessing(false);
    }
  };

  const resetCsvState = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvErrors([]);
    setCsvResults(null);
    setShowCsvPreview(false);
    setShowBulkInvite(false);
    setShowCsvSuccess(false);
    // Reset file input
    const fileInput = document.getElementById('csvFileInput');
    if (fileInput) fileInput.value = '';
  };

  // Download CSV template
  const downloadCsvTemplate = () => {
    const csvContent = 'email,firstName,lastName,role\njohn.doe@example.com,John,Doe,member\njane.smith@example.com,Jane,Smith,admin\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'team_members_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Define role badge style based on role
  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return (
          <span className="text-sm font-medium text-[#181818] dark:text-white capitalize">
            Owner
          </span>
        );
      case 'admin':
        return (
          <span className="text-sm font-medium text-[#181818] dark:text-white capitalize">
            Admin
          </span>
        );
      default:
        return (
          <span className="text-sm font-medium text-[#181818] dark:text-white capitalize">
            Member
          </span>
        );
    }
  };
  
  // Define status badge style based on status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="text-sm font-medium text-green-600 dark:text-green-400">
            Active
          </span>
        );
      case 'invited':
        return (
          <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
            Invited
          </span>
        );
      case 'inactive':
        return (
          <span className="text-sm font-medium text-red-600 dark:text-red-400">
            Inactive
          </span>
        );
      default:
        return null;
    }
  };

  // Update the invitation form JSX
  const renderInviteForm = () => (
    <div className="p-4 bg-gray-50 dark:bg-[#222] rounded-2xl border border-gray-200 dark:border-[#2a2a2a] mt-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-[#181818] dark:text-white">Invite Team Member</h3>
        <button
          type="button"
          onClick={() => setShowBulkInvite(true)}
          className="account-modal-btn inline-flex items-center px-3 py-1.5 border border-[#d4d4d4] shadow-sm text-xs font-medium rounded-lg text-[#404040] bg-white hover:bg-[#f5f5f5] focus:outline-none dark:text-[#cccccc] dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
        >
          <DocumentArrowUpIcon className="h-3 w-3 mr-1" />
          Bulk Invite (CSV)
        </button>
      </div>
      <form onSubmit={handleInviteMember} className="mt-3">
        <div className="space-y-4">
          <div>
            <label htmlFor="inviteEmail" className="block text-xs font-medium text-[#404040] dark:text-[#cccccc]">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="inviteEmail"
              name="inviteEmail"
              value={inviteEmail}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              required
              className={`mt-1 block w-full border ${
                emailError ? 'border-red-300' : 'border-[#d4d4d4] dark:border-[#525252]'
              } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm bg-white dark:bg-[#121212] text-[#181818] dark:text-white`}
            />
            {emailError && <p className="mt-1 text-xs text-red-500">{emailError}</p>}
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="inviteFirstName" className="block text-xs font-medium text-[#404040] dark:text-[#cccccc]">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="inviteFirstName"
                name="inviteFirstName"
                value={inviteFirstName}
                onChange={handleFirstNameChange}
                onBlur={handleFirstNameBlur}
                required
                className={`mt-1 block w-full border ${
                  firstNameError ? 'border-red-300' : 'border-[#d4d4d4] dark:border-[#525252]'
                } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm bg-white dark:bg-[#121212] text-[#181818] dark:text-white`}
              />
              {firstNameError && <p className="mt-1 text-xs text-red-500">{firstNameError}</p>}
            </div>
            
            <div>
              <label htmlFor="inviteLastName" className="block text-xs font-medium text-[#404040] dark:text-[#cccccc]">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="inviteLastName"
                name="inviteLastName"
                value={inviteLastName}
                onChange={handleLastNameChange}
                onBlur={handleLastNameBlur}
                required
                className={`mt-1 block w-full border ${
                  lastNameError ? 'border-red-300' : 'border-[#d4d4d4] dark:border-[#525252]'
                } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm bg-white dark:bg-[#121212] text-[#181818] dark:text-white`}
              />
              {lastNameError && <p className="mt-1 text-xs text-red-500">{lastNameError}</p>}
            </div>
          </div>
          
          <div>
            <label htmlFor="inviteRole" className="block text-xs font-medium text-[#404040] dark:text-[#cccccc]">
              Role
            </label>
            <select
              id="inviteRole"
              name="inviteRole"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="mt-1 block w-full border border-[#d4d4d4] dark:border-[#525252] rounded-md shadow-sm py-2 px-3 focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white sm:text-sm bg-white dark:bg-[#121212] text-[#181818] dark:text-white"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex space-x-2">
          <button
            type="submit"
            disabled={inviting || checkingBilling}
            className="account-modal-btn inline-flex items-center px-4 py-2 border border-black shadow-sm text-sm font-medium rounded-lg text-white bg-black hover:bg-[#262626] focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-[#e0e0e0] disabled:opacity-50"
          >
            {checkingBilling ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white dark:border-black border-t-transparent rounded-full mr-2"></div>
                Checking billing...
              </>
            ) : inviting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white dark:border-black border-t-transparent rounded-full mr-2"></div>
                Sending invitation...
              </>
            ) : (
              'Send Invitation'
            )}
          </button>
          <button
            type="button"
            onClick={handleCancelInvite}
            className="account-modal-btn inline-flex items-center px-4 py-2 border border-[#d4d4d4] shadow-sm text-sm font-medium rounded-lg text-[#404040] bg-white hover:bg-[#f5f5f5] focus:outline-none dark:text-[#cccccc] dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  const handleBillingSuccess = async (response) => {
    console.log('Billing authorization successful:', response);
    
    // Check if this is a bulk invite
    if (billingDetails?.isBulkInvite) {
      // Process bulk invitations with billing
      try {
        const bulkResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/team-billing/authorize-and-bulk-invite-members`,
          {
            invitations: pendingInviteData,
            billingDetails: {
              ...billingDetails,
              billableInvites: billingDetails.memberInfo?.billableInvites
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        const { results, authorization } = bulkResponse.data;
        
        // Add successful invitations to members list
        if (results.invitations && results.invitations.length > 0) {
          const newMembers = results.invitations.map(inv => ({
            id: inv.id,
            email: inv.email,
            role: inv.role,
            status: 'invited',
            invitedAt: inv.invitedAt,
            firstName: inv.firstName,
            lastName: inv.lastName,
            name: `${inv.firstName || ''} ${inv.lastName || ''}`.trim() || null
          }));
          
          setMembers([...members, ...newMembers]);
        }

        // Show success message for bulk invitation
        const totalCost = authorization?.amount || 0;
        const billableCount = billingDetails.memberInfo?.billableInvites || 0;
        const freeCount = billingDetails.memberInfo?.freeInvites || 0;
        
        let successMessage = `Bulk invitation completed! ${results.successful} invitation${results.successful !== 1 ? 's' : ''} sent successfully.`;
        
        if (billableCount > 0) {
          successMessage += ` Prorated charges of ${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(totalCost)} for ${billableCount} additional member${billableCount !== 1 ? 's' : ''} will be billed when they join.`;
        }
        
        if (freeCount > 0) {
          successMessage += ` ${freeCount} member${freeCount !== 1 ? 's' : ''} added within plan limits.`;
        }
        
        toast.success(successMessage, { ...toastStyle.success, duration: 6000 });
        
        // Close CSV modal and reset state
        resetCsvState();
        
      } catch (error) {
        console.error('Error processing bulk invitations with billing:', error);
        toast.error('Failed to process bulk invitations after payment authorization', toastStyle.error);
      }
    } else {
      // Handle single invitation - existing logic
      if (response.message && response.message.includes('already')) {
        // Handle existing invitation - update members list
        const updatedMembers = members.map(member => 
          member.email === pendingInviteData.email 
            ? { ...member, status: 'invited', invitedAt: new Date().toISOString() }
            : member
        );
        setMembers(updatedMembers);
        
        // Show success message for existing invitation
        toast.success(
          `Invitation updated! A prorated charge of ${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(response.authorization.amount)} will be billed when they join. Full seat price applies on your next billing date.`,
          { ...toastStyle.success, duration: 6000 }
        );
      } else {
        // Handle new invitation - add to members list
        setMembers([...members, {
          id: response.invitation.id,
          email: response.invitation.email,
          role: response.invitation.role,
          status: 'invited',
          invitedAt: response.invitation.invitedAt,
          firstName: response.invitation.firstName,
          lastName: response.invitation.lastName,
          name: `${response.invitation.firstName || ''} ${response.invitation.lastName || ''}`.trim() || null
        }]);
        
        // Show success message for new invitation
        toast.success(
          `Team member invited! A prorated charge of ${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(response.authorization.amount)} will be billed when they join. Full seat price applies on your next billing date. Invitation expires in 7 days.`,
          { ...toastStyle.success, duration: 6000 }
        );
      }
    }
    
    // Reset form and close modals
    resetInviteForm();
    handleCloseBillingAuth();
  };

  return (
    <div className="space-y-6">
      {/* Organization Info Card */}
      <div className="p-4 bg-gray-50 dark:bg-[#222] rounded-2xl border border-gray-200 dark:border-[#2a2a2a]">
        <h3 className="text-sm font-medium text-[#181818] dark:text-white">Organization Details</h3>
        <div className="mt-3 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6">
          <div>
            <h4 className="text-xs font-medium text-[#737373] dark:text-[#999999]">Name</h4>
            <p className="mt-1 text-sm text-[#181818] dark:text-white">{organization.name}</p>
          </div>
          <div>
            <h4 className="text-xs font-medium text-[#737373] dark:text-[#999999]">Domain</h4>
            <p className="mt-1 text-sm text-[#181818] dark:text-white">
              {organization.domain && !organization.domain.includes('company-') 
                ? organization.domain 
                : (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('accountModalTab', 'domain');
                      window.dispatchEvent(new CustomEvent('open-account-modal', { detail: { tab: 'domain' } }));
                    }}
                    className="underline text-[#181818] dark:text-white cursor-pointer hover:opacity-80"
                    style={{ fontFamily: 'var(--chat-font-family)' }}
                  >
                    Configure domain
                  </button>
                )}
            </p>
          </div>
          <div>
            <h4 className="text-xs font-medium text-[#737373] dark:text-[#999999]">Your Role</h4>
            <div className="mt-1">{getRoleBadge(userRole)}</div>
          </div>
          <div>
            <h4 className="text-xs font-medium text-[#737373] dark:text-[#999999]">Team Size</h4>
            <p className="mt-1 text-sm text-[#181818] dark:text-white">{members.length} member{members.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Invite form */}
      {showInviteForm && renderInviteForm()}
      
      {/* Team Members header + Invite button */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-[#181818] dark:text-white">Team Members</h3>
        {userRole && ['owner', 'admin'].includes(userRole) && (
          <button
            onClick={() => setShowInviteForm(true)}
            className="account-modal-btn inline-flex items-center px-3 py-1.5 border border-black shadow-sm text-xs font-medium rounded-lg text-white bg-black hover:bg-[#262626] focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-[#e0e0e0]"
            disabled={showInviteForm}
          >
            <UserPlusIcon className="h-3.5 w-3.5 mr-1.5" />
            Invite Team Member
          </button>
        )}
      </div>

      {/* Members table */}
      {members.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-[#737373] dark:text-[#999999]">
            No team members found.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-[#404040]">
              <thead className="bg-gray-100 dark:bg-[#1A1A1A]">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Member
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Role
                  </th>
                  {['owner', 'admin'].includes(userRole) && (
                    <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-gray-50 dark:bg-[#222] divide-y divide-gray-200 dark:divide-[#404040]">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-100 dark:hover:bg-[#272727]">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {member.profileImage?.url ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={member.profileImage.url}
                              alt={member.name || member.email}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-[#d4e4f7] dark:bg-[#2a4365] flex items-center justify-center text-[#2563eb] dark:text-[#93c5fd] font-medium">
                              {(() => {
                                if (member.name) {
                                  const nameParts = member.name.split(' ');
                                  if (nameParts.length >= 2) {
                                    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
                                  }
                                  return member.name.charAt(0).toUpperCase();
                                }
                                return member.email?.charAt(0).toUpperCase() || '?';
                              })()}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-[#181818] dark:text-white">
                            {member.name || (user && member.userId === user._id ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || '-' : '-')}
                          </div>
                          <div className="text-sm text-[#737373] dark:text-[#999999]">
                            {member.email}
                          </div>
                          {/* Show expiration info for invited members */}
                          {member.status === 'invited' && (() => {
                            const daysLeft = calculateExpirationDays(member.invitedAt);
                            if (daysLeft !== null) {
                              if (daysLeft <= 0) {
                                return (
                                  <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                                    Invitation expired
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                    Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                  </div>
                                );
                              }
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {getStatusBadge(member.status)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {member.role === 'owner' || member.status === 'invited' || member.userId === user?._id || !['owner', 'admin'].includes(userRole) ? (
                        getRoleBadge(member.role)
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => setPendingRoleChange({ member, newRole: e.target.value })}
                          disabled={memberIdChangingRole === member.id}
                          className="text-sm font-medium text-[#181818] dark:text-white bg-transparent border border-gray-200 dark:border-[#404040] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent disabled:opacity-50"
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                    </td>
                    {['owner', 'admin'].includes(userRole) && (
                      <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-0.5">
                          {/* If the member is invited, show resend option */}
                          {member.status === 'invited' && (
                            <button
                              onClick={() => handleResendInvitation(member.id)}
                              disabled={resendingInvite && memberIdResending === member.id || checkingBilling && memberIdResending === member.id}
                              className="account-modal-btn p-1.5 rounded-lg text-[#737373] hover:text-[#525252] hover:bg-gray-100 dark:text-[#999999] dark:hover:text-[#cccccc] dark:hover:bg-[#272727]"
                              title="Resend invitation"
                            >
                              {(resendingInvite || checkingBilling) && memberIdResending === member.id ? (
                                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                              ) : (
                                <EnvelopeIcon className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          
                          {/* For all non-owner members that are not the current user, show remove option */}
                          {member.role !== 'owner' && member.userId !== user?._id && member.email !== user?.email && (
                            <button
                              onClick={() => {
                                setMemberToRemove(member);
                                setShowDeleteConfirm(true);
                              }}
                              className="account-modal-btn p-1.5 rounded-lg text-[#737373] hover:text-[#525252] hover:bg-gray-100 dark:text-[#999999] dark:hover:text-[#cccccc] dark:hover:bg-[#272727]"
                              title="Remove member"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Role Change Confirmation Portal */}
      {pendingRoleChange && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
            onClick={() => setPendingRoleChange(null)}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-[#181818] dark:text-white mb-2">Change role to {pendingRoleChange.newRole === 'admin' ? 'Admin' : 'Member'}?</h3>
            <p className="text-sm text-[#737373] dark:text-[#999999] mb-4">
              {pendingRoleChange.member.name || pendingRoleChange.member.email} will {pendingRoleChange.newRole === 'admin' ? 'gain' : 'lose'}:
            </p>
            <ul className="text-sm text-[#525252] dark:text-[#cccccc] space-y-2 mb-6 list-disc list-inside">
              {pendingRoleChange.newRole === 'admin' ? (
                <>
                  <li>Invite and remove team members</li>
                  <li>Change member roles</li>
                  <li>Manage organization settings</li>
                </>
              ) : (
                <>
                  <li>Ability to invite and remove team members</li>
                  <li>Ability to change member roles</li>
                  <li>Ability to manage organization settings</li>
                </>
              )}
            </ul>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingRoleChange(null)}
                disabled={memberIdChangingRole === pendingRoleChange.member.id}
                className="account-modal-btn px-4 py-2 text-sm font-medium rounded-lg text-[#525252] dark:text-[#999999] hover:bg-gray-100 dark:hover:bg-[#272727] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await handleChangeRole(pendingRoleChange.member, pendingRoleChange.newRole);
                  if (ok) setPendingRoleChange(null);
                }}
                disabled={memberIdChangingRole === pendingRoleChange.member.id}
                className="account-modal-btn px-4 py-2 text-sm font-medium rounded-lg bg-black dark:bg-white text-white dark:text-black hover:bg-[#262626] dark:hover:bg-[#e0e0e0] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {memberIdChangingRole === pendingRoleChange.member.id ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && memberToRemove && (
        <div 
          ref={deleteConfirmRef}
          className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-300 dark:border-red-700"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Confirm Removal</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>Are you sure you want to remove <span className="font-medium">{memberToRemove.name || memberToRemove.email}</span> from the organization? This action cannot be undone.</p>
              </div>
              <div className="mt-4 flex space-x-4">
                <button
                  type="button"
                  className="account-modal-btn inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none dark:text-red-300 dark:bg-red-900 dark:hover:bg-red-800"
                  onClick={handleRemoveMember}
                >
                  Yes, Remove
                </button>
                <button
                  type="button"
                  className="account-modal-btn inline-flex items-center px-3 py-2 border border-[#d4d4d4] text-sm leading-4 font-medium rounded-lg text-[#404040] bg-white hover:bg-[#f5f5f5] focus:outline-none dark:text-[#cccccc] dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* CSV Bulk Invite Modal */}
      {showBulkInvite && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#111111] rounded-2xl border border-[#dbdbdb] dark:border-[#444444] max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-black dark:text-white">Bulk Invite via CSV</h3>
                <button
                  onClick={resetCsvState}
                  className="text-[#a3a3a3] hover:text-[#525252] dark:hover:text-[#cccccc]"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {!showCsvPreview ? (
                <div>
                  <div className="mb-4">
                    <p className="text-sm text-[#525252] dark:text-[#999999] mb-2">
                      Upload a CSV file with team member information. The CSV should have the following columns:
                    </p>
                    <div className="bg-[#f5f5f5] dark:bg-[#1A1A1A] p-3 rounded-lg text-sm">
                      <code className="text-[#262626] dark:text-[#e0e0e0]">email, firstName, lastName, role (optional)</code>
                    </div>
                    <p className="text-xs text-[#737373] dark:text-[#999999] mt-2">
                      Role can be "member" or "admin". If not specified, defaults to "member".
                    </p>
                    
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={downloadCsvTemplate}
                        className="inline-flex items-center px-3 py-2 border border-[#d4d4d4] shadow-sm text-sm font-medium rounded-lg text-[#404040] bg-white hover:bg-[#f5f5f5] focus:outline-none dark:text-[#cccccc] dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Download Template
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <input
                      type="file"
                      id="csvFileInput"
                      accept=".csv"
                      onChange={handleCsvFileChange}
                      className="block w-full text-sm text-[#737373] dark:text-[#999999] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-black file:text-white dark:file:bg-white dark:file:text-black hover:file:bg-[#262626] dark:hover:file:bg-[#e0e0e0]"
                    />
                  </div>
                </div>
              ) : showCsvSuccess ? (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-800 mb-4">
                    <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-[#181818] dark:text-white mb-2">
                    Invitations Sent Successfully!
                  </h3>
                  <p className="text-sm text-[#525252] dark:text-[#999999]">
                    {csvResults?.successful} invitation{csvResults?.successful !== 1 ? 's' : ''} sent successfully
                  </p>
                  <p className="text-xs text-[#737373] dark:text-[#999999] mt-2">
                    All invitations will expire in 7 days if not accepted.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-[#181818] dark:text-white mb-2">
                      Preview ({csvData.length} valid entries)
                    </h4>
                    
                    {csvErrors.length > 0 && (
                      <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                        <h5 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">
                          Errors ({csvErrors.length}):
                        </h5>
                        <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                          {csvErrors.slice(0, 5).map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                          {csvErrors.length > 5 && (
                            <li className="text-xs">... and {csvErrors.length - 5} more errors</li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {csvData.length > 0 && (
                      <div className="max-h-60 overflow-y-auto border border-[#dbdbdb] dark:border-[#404040] rounded-lg">
                        <table className="min-w-full divide-y divide-[#dbdbdb] dark:divide-[#404040]">
                          <thead className="bg-[#f5f5f5] dark:bg-[#1A1A1A]">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase w-1/3">Email</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase w-1/4">First Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase w-1/4">Last Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase w-1/6">Role</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-[#737373] dark:text-[#999999] uppercase w-12">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-[#111111] divide-y divide-[#dbdbdb] dark:divide-[#404040]">
                            {csvData.map((row, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2">
                                  <input
                                    type="email"
                                    value={row.email}
                                    onChange={(e) => {
                                      const newData = [...csvData];
                                      newData[index].email = e.target.value;
                                      setCsvData(newData);
                                    }}
                                    className="w-full text-sm bg-transparent border-none focus:outline-none text-[#181818] dark:text-white"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={row.firstName}
                                    onChange={(e) => {
                                      const newData = [...csvData];
                                      newData[index].firstName = e.target.value;
                                      setCsvData(newData);
                                    }}
                                    className="w-full text-sm bg-transparent border-none focus:outline-none text-[#181818] dark:text-white"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={row.lastName}
                                    onChange={(e) => {
                                      const newData = [...csvData];
                                      newData[index].lastName = e.target.value;
                                      setCsvData(newData);
                                    }}
                                    className="w-full text-sm bg-transparent border-none focus:outline-none text-[#181818] dark:text-white"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <select
                                    value={row.role}
                                    onChange={(e) => {
                                      const newData = [...csvData];
                                      newData[index].role = e.target.value;
                                      setCsvData(newData);
                                    }}
                                    className="w-full text-sm bg-transparent border-none focus:outline-none text-[#181818] dark:text-white capitalize"
                                  >
                                    <option value="member">Member</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    onClick={() => {
                                      const newData = csvData.filter((_, i) => i !== index);
                                      setCsvData(newData);
                                    }}
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                    title="Remove row"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center mb-4">
                    <button
                      onClick={() => {
                        const newRow = {
                          email: '',
                          firstName: '',
                          lastName: '',
                          role: 'member',
                          rowNumber: csvData.length + 1
                        };
                        setCsvData([...csvData, newRow]);
                      }}
                      className="inline-flex items-center px-3 py-2 border border-[#d4d4d4] shadow-sm text-sm font-medium rounded-lg text-[#404040] bg-white hover:bg-[#f5f5f5] focus:outline-none dark:text-[#cccccc] dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
                    >
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      Add Row
                    </button>
                    
                    <p className="text-xs text-[#737373] dark:text-[#999999]">
                      Click on any field to edit. Use the trash icon to remove rows.
                    </p>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={handleBulkInvite}
                      disabled={csvProcessing || csvData.length === 0}
                      className={`flex-1 inline-flex justify-center items-center px-4 py-2 border text-sm font-medium rounded-lg ${
                        csvProcessing || csvData.length === 0
                          ? 'bg-[#d4d4d4] dark:bg-[#333333] cursor-not-allowed border-[#d4d4d4] dark:border-[#444444] text-[#737373] dark:text-[#999999]'
                          : 'bg-black dark:bg-white border-black text-white dark:text-black dark:border-white hover:bg-[#262626] dark:hover:bg-[#e0e0e0]'
                      }`}
                    >
                      {csvProcessing ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                          Sending invitations...
                        </>
                      ) : (
                        `Send ${csvData.length} Invitation${csvData.length !== 1 ? 's' : ''}`
                      )}
                    </button>
                    <button
                      onClick={resetCsvState}
                      disabled={csvProcessing}
                      className="px-4 py-2 border border-[#d4d4d4] text-sm font-medium rounded-lg text-[#404040] bg-white hover:bg-[#f5f5f5] dark:text-[#cccccc] dark:bg-[#111111] dark:border-[#222222] dark:hover:bg-[#111111]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Billing Authorization Modal */}
      <BillingAuthModal
        isOpen={showBillingAuth}
        onClose={handleCloseBillingAuth}
        billingDetails={billingDetails}
        inviteData={pendingInviteData}
        onSuccess={handleBillingSuccess}
        darkMode={darkMode}
      />
    </div>
  );
};

export default MembersSection; 